"use client";

import { FC, useCallback, useState } from "react";
import useModalOutsideClick from "@repo/shared-ui/useModalOutsideClick";
import { trackComponent } from "@repo/analytics";
import { dlEventConstants } from "@repo/analytics/data-layer.constants";
import {
  IFiiAndDiiData,
  IFiiAndDiiIndexData,
} from "../../interfaces/StockApi.interface";
import StockListNotFound from "../ClpStockCards/StockListNotFound";
import { getCustomerIdForAnalytics } from "../../utils/analytics-utils";

export interface FiiDiiRow {
  label: string;
  value: number | null;
}

interface FiiAndDiiTradingActivityProps {
  data: IFiiAndDiiData;
  heading?: string;
  date?: string;
  unitLabel?: string;
  className?: string;
  cashLabel?: string;
  futuresLabel?: string;
  componentPosition?: string;
}

const barScale = (value: number, maxAbs: number): number => {
  const safeValue = Number(value) || 0;
  const safeMax = Number(maxAbs) || 0;
  return safeMax > 0 ? (Math.abs(safeValue) / safeMax) * 100 : 0;
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatValue = (value: number): string => {
  const safe = Number(value) || 0;
  return (safe >= 0 ? "" : "-") + Math.abs(safe).toFixed(2);
};

const parseNumericField = (v: unknown): number | null => {
  if (v == null) return null;
  if (typeof v !== "number") return null;
  if (Number.isNaN(v)) return null;
  return v;
};

const getRowsByType = (
  item: IFiiAndDiiIndexData | undefined,
  type: "cash" | "futures",
): FiiDiiRow[] => {
  if (!item) return [];
  if (type === "cash") {
    return [
      { label: "FII Cash", value: parseNumericField(item.fiiCash) },
      { label: "DII Cash", value: parseNumericField(item.diiCash) },
    ];
  }
  return [
    { label: "FII Futures", value: parseNumericField(item.fiiFuture) },
    { label: "FII Options", value: parseNumericField(item.fiiOption) },
  ];
};

export const FiiAndDiiTradingActivity: FC<FiiAndDiiTradingActivityProps> = ({
  data,
  heading = "FII & DII trading activity",
  unitLabel = "Rs. Crores",
  date: dateProp,
  className,
  cashLabel = "Cash",
  futuresLabel = "Futures",
  componentPosition,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { handleBackdropClick, handleModalDialogClick } = useModalOutsideClick({
    onClose: () => setIsDropdownOpen(false),
  });
  const [selectedType, setSelectedType] = useState<"cash" | "futures">("cash");

  const trackFilterBarClick = useCallback(
    (ctaText: string) => {
      void (async () => {
        try {
          const customerId = await getCustomerIdForAnalytics();
          await trackComponent({
            componentName: "filter_bar_selection_chips_chevron",
            sectionTitle: heading,
            eventType: dlEventConstants.CLICK,
            clickType: "filter_bar_selection_chips_chevron_icon_click",
            ctaText,
            productCategory: "investment",
            cleverTapOverrides: {
              EP_PROPERTY_TYPE: "clp",
              EP_PRODUCT_NAME: "pre and post market insights - trading mall",
              EP_REFERENCE_ID: "FII_&_DII_trading_activity",
              EP_ENTRYPOINT: "horizontal/entrypoint",
              ...(customerId && { EP_CUSTOMER_ID: customerId }),
              ...(componentPosition && { EP_SR_NUMBER: componentPosition }),
            },
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to track FII & DII filter interaction", error);
        }
      })();
    },
    [componentPosition, heading],
  );

  const handleFilterChipClick = () => {
    trackFilterBarClick(selectedType === "cash" ? cashLabel : futuresLabel);
    setIsDropdownOpen((open) => !open);
  };

  const handleFilterSelection = (type: "cash" | "futures") => {
    trackFilterBarClick(type === "cash" ? cashLabel : futuresLabel);
    setSelectedType(type);
    setIsDropdownOpen(false);
  };


  const raw = data?.data;
  let dataList: IFiiAndDiiIndexData[];
  if (Array.isArray(raw)) {
    dataList = raw;
  } else if (raw == null) {
    dataList = [];
  } else {
    dataList = [raw];
  }
  const firstItem = dataList[0];
  const rowsByType: Record<"cash" | "futures", FiiDiiRow[]> = {
    cash: getRowsByType(firstItem, "cash"),
    futures: getRowsByType(firstItem, "futures"),
  };

  const resolvedDateRaw = dateProp ?? firstItem?.date ?? "";

  const dateDisplay = (() => {
    if (!resolvedDateRaw.trim()) return "-";
    const formatted = formatDate(resolvedDateRaw);
    return formatted || "-";
  })();
  
  const unitDisplay =
    unitLabel != null && String(unitLabel).trim() !== "" ? unitLabel : "-";

  return (
    <div
      className={[
        "flex max-w-[864px] flex-col gap-[12px] p-4",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="relative flex items-center justify-between">
        <h2 className="font-rubik text-[18px] font-medium text-[#1a1a1a] md:text-[20px]">
          {heading}
        </h2>
        <button
          type="button"
          onClick={handleFilterChipClick}
          className="flex cursor-pointer z-4 items-center justify-between gap-2 rounded-[4px] border border-[#E5E5E5] px-[12px] py-[8px] pl-[12px] pr-[12px] md:text-[16px] text-[14px] font-[400px] text-[#1a1a1a] md:h-[48px]"
        >
          {selectedType === "cash" ? cashLabel : futuresLabel}
          <svg
            className={`block h-[6px] w-[12px] shrink-0 text-[#1A1A1A] transition-transform md:h-[8px] md:w-[16px] ${
              isDropdownOpen ? "rotate-180" : ""
            }`}
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              d="M1 1L7 7L13 1"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {isDropdownOpen && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-4 bg-transparent"
              onClick={handleBackdropClick}
              aria-label="Close type dropdown backdrop"
            />
            <div
              onClick={handleModalDialogClick}
              className="absolute min-w-[93px] right-[0px] top-[calc(100%+8px)] z-4 rounded-[4px] border border-gray-200 bg-white px-[8px] py-[4px]"
            >
              {(["cash", "futures"] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  className="flex w-full text-left cursor-pointer gap-[10px] rounded-[4px] px-[8px] py-[4px] text-[12px] text-[#1a1a1a] md:hover:bg-gray-50 md:text-[14px]"
                  onClick={() => handleFilterSelection(type)}
                >
                  <span className="flex-1">
                    {type === "cash" ? cashLabel : futuresLabel}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-[8px] py-[12px]">
        {rowsByType.cash.length === 0 && rowsByType.futures.length === 0 ? (
          <div className="flex justify-center py-8">
            <StockListNotFound
              errorMessage="Value not available"
              stockNotFoundImgUrl="https://cms-assets.bajajfinserv.in/is/image/bajajfinance/no-stocks"
            />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between py-[12px] text-[14px] font-[400] text-[#1a1a1a] md:text-[14px]">
              <span>{dateDisplay}</span>
              <span>{unitDisplay}</span>
            </div>
            <div className="border-t border-[#E5E5E5]"></div>
            {(["cash", "futures"] as const).map((type) => {
              const rows = rowsByType[type];
              const numericMagnitudes = rows
                .map((r) => r.value)
                .filter((v): v is number => v != null)
                .map((v) => Math.abs(v));
              const maxAbs = numericMagnitudes.length
                ? Math.max(...numericMagnitudes, 1)
                : 1;
              const isVisible = selectedType === type;

              return (
                <div
                  key={type}
                  className={isVisible ? "flex flex-col gap-[8px]" : "hidden"}
                  data-fii-dii-type={type}
                >
                  {rows.map((row, index) => {
                    const value = row.value;
                    const hasValue = value != null;
                    const isPositive = hasValue && value >= 0;
                    const width = hasValue ? barScale(value, maxAbs) : 0;
                    let valueToneClass = "text-[#1a1a1a]";
                    if (hasValue) {
                      valueToneClass = isPositive
                        ? "text-[#067161]"
                        : "text-[#B40000]";
                    }
                    return (
                      <div key={row.label}>
                        {index > 0 && (
                          <div className="mb-3 h-px w-full bg-[#E5E5E5]" />
                        )}
                        <div className="flex items-center justify-between gap-3">
                          <span className="min-w-[72px] text-[14px] font-[400] text-[#1a1a1a] md:text-[16px]">
                            {row.label}
                          </span>
                          <div className="flex h-[12px] w-[120px] items-stretch overflow-hidden md:w-[408px]">
                            {/* Left (loss / red) half */}
                            <div className="relative h-[12px] w-[56px] md:w-[215px]">
                              <div
                                className="absolute top-0 h-[12px]"
                                style={{
                                  right: 0,
                                  width: isPositive ? "0%" : `${width}%`,
                                  backgroundColor: "#B40000",
                                }}
                              />
                            </div>
                            {/* Right (gain / green) half */}
                            <div className="relative h-[12px] w-[56px] md:w-[215px]">
                              <div
                                className="absolute top-0 h-[12px]"
                                style={{
                                  left: 0,
                                  width: isPositive ? `${width}%` : "0%",
                                  backgroundColor: "#089E86",
                                }}
                              />
                            </div>
                          </div>
                          <span
                            className={`min-w-[72px] text-right text-[14px] md:text-[16px] ${valueToneClass}`}
                          >
                            {hasValue ? formatValue(value) : "-"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default FiiAndDiiTradingActivity;
